export type AudioCallStatsMetrics = Record<string, number | string>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

export function snapshotAudioCallStats(report: RTCStatsReport): AudioCallStatsMetrics {
  const metrics: AudioCallStatsMetrics = {};
  const byId = new Map<string, Record<string, unknown>>();
  let pairId: string | null = null;
  report.forEach((value: unknown): void => {
    if (!isRecord(value)) return;
    if (typeof value.id === 'string') byId.set(value.id, value);
    if (value.type === 'transport' && typeof value.selectedCandidatePairId === 'string') pairId = value.selectedCandidatePairId;
    if (value.kind !== 'audio' && value.mediaType !== 'audio') return;
    const fields = value.type === 'inbound-rtp'
      ? {packetsReceived: 'inbound_packets', bytesReceived: 'inbound_bytes', packetsLost: 'packets_lost', totalAudioEnergy: 'total_audio_energy', concealedSamples: 'concealed_samples'}
      : value.type === 'outbound-rtp' ? {packetsSent: 'outbound_packets', bytesSent: 'outbound_bytes'} : {};
    for (const [field, name] of Object.entries(fields)) {
      const number = value[field];
      if (typeof number === 'number' && Number.isFinite(number)) metrics[name] = Number(metrics[name] ?? 0) + number;
    }
    if (value.type === 'inbound-rtp' && typeof value.jitter === 'number') metrics.jitter_ms = value.jitter * 1000;
    if (value.type === 'inbound-rtp' && typeof value.audioLevel === 'number') metrics.audio_level = value.audioLevel;
  });
  const pair: unknown = pairId ? byId.get(pairId) : null;
  if (isRecord(pair)) {
    if (typeof pair.currentRoundTripTime === 'number') metrics.rtt_ms = pair.currentRoundTripTime * 1000;
    const local: unknown = typeof pair.localCandidateId === 'string' ? byId.get(pair.localCandidateId) : null;
    const remote: unknown = typeof pair.remoteCandidateId === 'string' ? byId.get(pair.remoteCandidateId) : null;
    if (isRecord(local)) {
      if (typeof local.candidateType === 'string') metrics.local_candidate_type = local.candidateType;
      if (typeof local.protocol === 'string') metrics.protocol = local.protocol;
      if (typeof local.relayProtocol === 'string') metrics.relay_protocol = local.relayProtocol;
      if (typeof local.url === 'string') {
        const host = local.url.match(/^turns?:([^:?/]+)(?::\d+)?(?:\?transport=(?:udp|tcp))?$/i)?.[1]?.toLowerCase();
        if (host === 'stun-spb.pyjam.com') metrics.turn_alias = 'timeweb';
        if (host === 'app.pyjam.com') metrics.turn_alias = 'app_geo';
        if (host === 'dev.pyjam.com') metrics.turn_alias = 'dev';
      }
    }
    if (isRecord(remote) && typeof remote.candidateType === 'string') metrics.remote_candidate_type = remote.candidateType;
  }
  return metrics;
}

export class AudioCallStatsSampler {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private generation: number = 0;
  private finalReport: (() => void) | null = null;

  public stop(): void {
    this.generation += 1;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.finalReport?.();
    this.finalReport = null;
  }

  public start(peer: RTCPeerConnection, report: (stage: string, metrics: AudioCallStatsMetrics) => void): void {
    this.stop();
    const generation = this.generation;
    const started = Date.now();
    let receivedMedia = false;
    let sentMedia = false;
    let sentAndReceivedMedia = false;
    let decodedAudio = false;
    let statsFailed = false;
    let lastSample = -Infinity;
    const sample = async (): Promise<void> => {
      if (generation !== this.generation || peer.connectionState === 'closed') return;
      try {
        const stats = await peer.getStats();
        if (generation !== this.generation) return;
        const metrics = snapshotAudioCallStats(stats);
        const sampledAt = Date.now();
        this.finalReport = (): void => report('rtp.summary', {...metrics, snapshot_age_ms: Date.now() - sampledAt});
        if (!sentMedia && Number(metrics.outbound_packets) > 0) {
          sentMedia = true;
          report('rtp.first_outbound', metrics);
        }
        if (!receivedMedia && Number(metrics.inbound_packets) > 0) {
          receivedMedia = true;
          report('rtp.first_inbound', metrics);
        }
        if (!sentAndReceivedMedia && receivedMedia && Number(metrics.outbound_packets) > 0) {
          sentAndReceivedMedia = true;
          report('rtp.first_bidirectional', metrics);
        }
        if (!decodedAudio && Number(metrics.total_audio_energy) > 0) {
          decodedAudio = true;
          report('rtp.first_decoded_audio', metrics);
        }
        if (sampledAt - lastSample >= 30000) {
          lastSample = sampledAt;
          report('rtp.sample', metrics);
        }
      } catch {
        if (generation === this.generation && !statsFailed) {
          statsFailed = true;
          report('rtp.stats_failed', {});
        }
      }
      if (generation === this.generation) {
        this.timer = setTimeout((): void => { void sample(); }, Date.now() - started < 10000 ? 1000 : 30000);
      }
    };
    void sample();
  }
}
